import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "d-flex align-center" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "d-flex align-center mb-4" }
const _hoisted_4 = { class: "text-body-1 text-medium-emphasis" }
const _hoisted_5 = { class: "position-relative" }
const _hoisted_6 = { class: "d-flex align-center justify-center fill-height" }
const _hoisted_7 = { class: "text-h6 font-weight-bold mb-2" }
const _hoisted_8 = { class: "text-body-2 text-medium-emphasis mb-3 line-clamp-2" }
const _hoisted_9 = { class: "d-flex align-center justify-space-between" }
const _hoisted_10 = {
  key: 1,
  class: "text-center pa-12"
}
const _hoisted_11 = { class: "text-h5 font-weight-bold" }
const _hoisted_12 = {
  key: 0,
  class: "mt-3"
}
const _hoisted_13 = { class: "d-flex align-center mb-2" }
const _hoisted_14 = { class: "position-relative d-inline-block" }
const _hoisted_15 = ["src"]

import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AdminLayout from './components/AdminLayout.vue'
import { storage } from './firebase'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { useAuthStore } from './stores/authStore'


export default /*@__PURE__*/_defineComponent({
  __name: 'AdminRewards',
  setup(__props) {

const router = useRouter()
const saving = ref(false)
const storeItems = ref([])
const showStoreItemModal = ref(false)
const editingItem = ref(null)
const imageFile = ref(null)
const imagePreview = ref('')

const storeItemForm = ref({
  name: '',
  description: '',
  points: 0,
  quantity: 0,
  imageUrl: ''
})

const formErrors = ref({
  name: '',
  points: '',
  quantity: ''
})

const validateForm = () => {
  formErrors.value = {
    name: '',
    points: '',
    quantity: ''
  }
  
  let isValid = true
  
  if (!storeItemForm.value.name.trim()) {
    formErrors.value.name = 'Name is required'
    isValid = false
  }
  
  if (!storeItemForm.value.points || storeItemForm.value.points <= 0) {
    formErrors.value.points = 'Tokens must be greater than 0'
    isValid = false
  }
  
  if (storeItemForm.value.quantity === null || storeItemForm.value.quantity === undefined || storeItemForm.value.quantity < 0) {
    formErrors.value.quantity = 'Quantity must be 0 or greater'
    isValid = false
  }
  
  return isValid
}

const handleImageChange = (file) => {
  if (!file) {
    imagePreview.value = ''
    return
  }
  
  const reader = new FileReader()
  reader.onload = (e) => {
    imagePreview.value = e.target.result
  }
  reader.readAsDataURL(file)
}

const uploadImage = async (file: File): Promise<string | null> => {
  if (!file) return null
  
  try {
    // Create a unique filename
    const timestamp = Date.now()
    const filename = `store-items/${timestamp}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
    
    // Create a reference to the file location in Firebase Storage
    const fileRef = storageRef(storage, filename)
    
    // Upload the file
    const snapshot = await uploadBytes(fileRef, file)
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref)
    return downloadURL
  } catch (error) {
    console.error('Error uploading image:', error)
    throw error
  }
}

const saveStoreItem = async () => {
  if (!validateForm()) {
    alert('Please fill in all required fields correctly.')
    return
  }

  if (!editingItem.value && !imageFile.value && !imagePreview.value) {
    alert('Please select an image')
    return
  }

  saving.value = true
  
  try {
    let imageUrl = storeItemForm.value.imageUrl
    
    if (imageFile.value) {
      imageUrl = await uploadImage(imageFile.value)
    }
    
    const payload = {
      ...storeItemForm.value,
      imageUrl
    }
    
    const url = editingItem.value
      ? `${process.env.VUE_APP_API_BASE_URL}/store/items/${editingItem.value.id}`
      : `${process.env.VUE_APP_API_BASE_URL}/store/items`
      
    const method = editingItem.value ? 'PUT' : 'POST'
    
    const response = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    })
    
    if (!response.ok) {
      throw new Error('Failed to save store item')
    }
    
    await fetchStoreItems()
    closeStoreItemModal()
  } catch (error) {
    console.error('Error saving store item:', error)
    alert('Failed to save store item: ' + error.message)
  
    saving.value = false
  }
}

const closeStoreItemModal = () => {
  showStoreItemModal.value = false
  editingItem.value = null
  storeItemForm.value = {
    name: '',
    description: '',
    points: 0,
    quantity: 0,
    imageUrl: ''
  }
  formErrors.value = {
    name: '',
    points: '',
    quantity: ''
  }
  imageFile.value = null
  imagePreview.value = ''
  saving.value = false
}

const clearImage = () => {
  imageFile.value = null
  imagePreview.value = ''
  storeItemForm.value.imageUrl = ''
}

const editStoreItem = (item) => {
  editingItem.value = item
  storeItemForm.value = { 
    name: item.name,
    description: item.description,
    points: item.points,
    quantity: item.quantity,
    imageUrl: item.imageUrl
  }
  formErrors.value = {
    name: '',
    points: '',
    quantity: ''
  }
  if (item.imageUrl) {
    imagePreview.value = item.imageUrl
  }
  showStoreItemModal.value = true
}

const deleteStoreItem = async (itemId) => {
  if (!confirm('Are you sure you want to delete this item?')) return

  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/store/items/${itemId}`, {
      method: 'DELETE',
      credentials: 'include'
    })

    if (response.ok) {
      await fetchStoreItems()
    }
  } catch (error) {
    console.error('Error deleting store item:', error)
  }
}

const fetchStoreItems = async () => {
  try {
    const response = await fetch(`${process.env.VUE_APP_API_BASE_URL}/store/items`, {
      credentials: 'include'
    })
    if (response.ok) {
      storeItems.value = await response.json()
    }
  } catch (error) {
    console.error('Error fetching store items:', error)
  }
}

function openStoreItemModal() {
  editingItem.value = null
  storeItemForm.value = {
    name: '',
    description: '',
    points: 0,
    quantity: 0,
    imageUrl: ''
  }
  formErrors.value = {
    name: '',
    points: '',
    quantity: ''
  }
  showStoreItemModal.value = true
}

const { checkAuth } = useAuthStore()

onMounted(async () => {
  try {
    // Check if user is admin using auth store
    const authData = await checkAuth()
    if (!authData.isAdmin) {
      router.push('/dashboard')
      return
    }

    await fetchStoreItems()
  } catch (error) {
    console.error('Error loading admin rewards:', error)
  }
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_img = _resolveComponent("v-img")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createVNode(_component_v_container, {
        fluid: "",
        class: "pa-6"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, {
            elevation: "3",
            class: "rounded-lg"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "d-flex align-center justify-space-between pa-6 bg-gradient-primary" }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_1, [
                    _createVNode(_component_v_icon, {
                      icon: "mdi-gift",
                      class: "mr-3",
                      size: "32",
                      color: "white"
                    }),
                    _cache[6] || (_cache[6] = _createElementVNode("span", { class: "text-h5 font-weight-bold text-white" }, "Rewards Management", -1))
                  ]),
                  _createVNode(_component_v_btn, {
                    color: "white",
                    variant: "elevated",
                    "prepend-icon": "mdi-plus",
                    size: "large",
                    class: "px-6",
                    onClick: openStoreItemModal
                  }, {
                    default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                      _createTextVNode(" Add Reward ", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "pa-6" }, {
                default: _withCtx(() => [
                  (storeItems.value.length > 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createVNode(_component_v_row, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_col, { cols: "12" }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_3, [
                                  _createVNode(_component_v_icon, {
                                    icon: "mdi-information",
                                    color: "info",
                                    class: "mr-2"
                                  }),
                                  _createElementVNode("span", _hoisted_4, _toDisplayString(storeItems.value.length) + " reward" + _toDisplayString(storeItems.value.length !== 1 ? 's' : '') + " available ", 1)
                                ])
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_row, null, {
                          default: _withCtx(() => [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(storeItems.value, (item) => {
                              return (_openBlock(), _createBlock(_component_v_col, {
                                key: item.id,
                                cols: "12",
                                md: "6",
                                lg: "4"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_card, {
                                    elevation: "2",
                                    class: _normalizeClass(["h-100 reward-card", { 'out-of-stock': item.quantity === 0 }])
                                  }, {
                                    default: _withCtx(() => [
                                      _createElementVNode("div", _hoisted_5, [
                                        _createVNode(_component_v_img, {
                                          src: item.imageUrl,
                                          alt: item.name,
                                          height: "200",
                                          cover: "",
                                          class: "rounded-t-lg"
                                        }, {
                                          placeholder: _withCtx(() => [
                                            _createElementVNode("div", _hoisted_6, [
                                              _createVNode(_component_v_icon, {
                                                icon: "mdi-image",
                                                size: "48",
                                                color: "grey-lighten-1"
                                              })
                                            ])
                                          ]),
                                          _: 2
                                        }, 1032, ["src", "alt"]),
                                        (item.quantity === 0)
                                          ? (_openBlock(), _createBlock(_component_v_chip, {
                                              key: 0,
                                              color: "error",
                                              variant: "flat",
                                              size: "small",
                                              class: "position-absolute top-0 right-0 ma-2"
                                            }, {
                                              default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                                                _createTextVNode(" Out of Stock ", -1)
                                              ]))]),
                                              _: 1
                                            }))
                                          : _createCommentVNode("", true),
                                        _createVNode(_component_v_chip, {
                                          color: item.quantity > 10 ? 'success' : item.quantity > 5 ? 'warning' : 'error',
                                          variant: "flat",
                                          size: "small",
                                          class: "position-absolute top-0 left-0 ma-2"
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(" Qty: " + _toDisplayString(item.quantity), 1)
                                          ]),
                                          _: 2
                                        }, 1032, ["color"])
                                      ]),
                                      _createVNode(_component_v_card_text, { class: "pa-4" }, {
                                        default: _withCtx(() => [
                                          _createElementVNode("h3", _hoisted_7, _toDisplayString(item.name), 1),
                                          _createElementVNode("p", _hoisted_8, _toDisplayString(item.description), 1),
                                          _createElementVNode("div", _hoisted_9, [
                                            _createVNode(_component_v_chip, {
                                              color: "primary",
                                              variant: "elevated",
                                              size: "large",
                                              class: "font-weight-bold"
                                            }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_icon, {
                                                  icon: "mdi-currency-usd",
                                                  class: "mr-1"
                                                }),
                                                _createTextVNode(" " + _toDisplayString(item.points) + " Tokens ", 1)
                                              ]),
                                              _: 2
                                            }, 1024)
                                          ])
                                        ]),
                                        _: 2
                                      }, 1024),
                                      _createVNode(_component_v_card_actions, { class: "pa-4 pt-0" }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_btn, {
                                            "prepend-icon": "mdi-pencil",
                                            variant: "outlined",
                                            size: "default",
                                            color: "primary",
                                            class: "mr-2 action-btn",
                                            onClick: ($event: any) => (editStoreItem(item))
                                          }, {
                                            default: _withCtx(() => [
                                              _cache[10] || (_cache[10] = _createTextVNode(" Edit ", -1)),
                                              _createVNode(_component_v_tooltip, {
                                                activator: "parent",
                                                location: "top"
                                              }, {
                                                default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                                  _createTextVNode("Edit Reward", -1)
                                                ]))]),
                                                _: 1
                                              })
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick"]),
                                          _createVNode(_component_v_btn, {
                                            "prepend-icon": "mdi-delete",
                                            variant: "outlined",
                                            size: "default",
                                            color: "error",
                                            class: "action-btn",
                                            onClick: ($event: any) => (deleteStoreItem(item.id))
                                          }, {
                                            default: _withCtx(() => [
                                              _cache[12] || (_cache[12] = _createTextVNode(" Delete ", -1)),
                                              _createVNode(_component_v_tooltip, {
                                                activator: "parent",
                                                location: "top"
                                              }, {
                                                default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                                  _createTextVNode("Delete Reward", -1)
                                                ]))]),
                                                _: 1
                                              })
                                            ]),
                                            _: 2
                                          }, 1032, ["onClick"]),
                                          _createVNode(_component_v_spacer),
                                          _createVNode(_component_v_chip, {
                                            color: item.quantity > 10 ? 'success' : item.quantity > 5 ? 'warning' : 'error',
                                            variant: "flat",
                                            size: "small"
                                          }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_icon, {
                                                icon: item.quantity > 10 ? 'mdi-check-circle' : item.quantity > 5 ? 'mdi-alert-circle' : 'mdi-close-circle',
                                                class: "mr-1"
                                              }, null, 8, ["icon"]),
                                              _createTextVNode(" " + _toDisplayString(item.quantity > 10 ? 'In Stock' : item.quantity > 5 ? 'Low Stock' : 'Critical'), 1)
                                            ]),
                                            _: 2
                                          }, 1032, ["color"])
                                        ]),
                                        _: 2
                                      }, 1024)
                                    ]),
                                    _: 2
                                  }, 1032, ["class"])
                                ]),
                                _: 2
                              }, 1024))
                            }), 128))
                          ]),
                          _: 1
                        })
                      ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createVNode(_component_v_icon, {
                          icon: "mdi-gift-off",
                          size: "80",
                          color: "grey-lighten-1",
                          class: "mb-4"
                        }),
                        _cache[14] || (_cache[14] = _createElementVNode("h3", { class: "text-h5 text-grey-darken-1 mb-2" }, "No Rewards Available", -1)),
                        _cache[15] || (_cache[15] = _createElementVNode("p", { class: "text-body-1 text-medium-emphasis mb-6" }, " Get started by adding your first reward to the store ", -1)),
                        _createVNode(_component_v_btn, {
                          color: "primary",
                          variant: "elevated",
                          "prepend-icon": "mdi-plus",
                          size: "large",
                          onClick: openStoreItemModal
                        }, {
                          default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                            _createTextVNode(" Add Your First Reward ", -1)
                          ]))]),
                          _: 1
                        })
                      ]))
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showStoreItemModal.value,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showStoreItemModal).value = $event)),
        "max-width": "600px",
        persistent: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, { class: "rounded-lg" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "d-flex align-center pa-6 bg-gradient-primary text-white" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_icon, {
                    icon: "mdi-gift",
                    class: "mr-3"
                  }),
                  _createElementVNode("span", _hoisted_11, _toDisplayString(editingItem.value ? 'Edit' : 'Create') + " Reward ", 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, { class: "pa-6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_form, {
                    onSubmit: _withModifiers(saveStoreItem, ["prevent"])
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_container, {
                        fluid: "",
                        class: "pa-0"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_row, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_col, { cols: "12" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: storeItemForm.value.name,
                                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((storeItemForm.value.name) = $event)),
                                    label: "Reward Name",
                                    required: "",
                                    variant: "outlined",
                                    density: "comfortable",
                                    "error-messages": formErrors.value.name,
                                    error: !!formErrors.value.name,
                                    "prepend-inner-icon": "mdi-tag",
                                    placeholder: "Enter reward name"
                                  }, null, 8, ["modelValue", "error-messages", "error"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, { cols: "12" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_textarea, {
                                    modelValue: storeItemForm.value.description,
                                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((storeItemForm.value.description) = $event)),
                                    label: "Description",
                                    variant: "outlined",
                                    density: "comfortable",
                                    rows: "3",
                                    "prepend-inner-icon": "mdi-text",
                                    placeholder: "Describe what this reward offers"
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, { cols: "6" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: storeItemForm.value.points,
                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((storeItemForm.value.points) = $event)),
                                    modelModifiers: { number: true },
                                    label: "Token Cost",
                                    type: "number",
                                    required: "",
                                    min: "1",
                                    variant: "outlined",
                                    density: "comfortable",
                                    "error-messages": formErrors.value.points,
                                    error: !!formErrors.value.points,
                                    "prepend-inner-icon": "mdi-currency-usd",
                                    placeholder: "0"
                                  }, null, 8, ["modelValue", "error-messages", "error"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, { cols: "6" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: storeItemForm.value.quantity,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((storeItemForm.value.quantity) = $event)),
                                    modelModifiers: { number: true },
                                    label: "Available Quantity",
                                    type: "number",
                                    required: "",
                                    min: "0",
                                    variant: "outlined",
                                    density: "comfortable",
                                    "error-messages": formErrors.value.quantity,
                                    error: !!formErrors.value.quantity,
                                    "prepend-inner-icon": "mdi-package-variant",
                                    placeholder: "0"
                                  }, null, 8, ["modelValue", "error-messages", "error"])
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_v_col, { cols: "12" }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_file_input, {
                                    modelValue: imageFile.value,
                                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((imageFile).value = $event)),
                                    label: "Reward Image",
                                    accept: "image/*",
                                    variant: "outlined",
                                    density: "comfortable",
                                    "prepend-icon": "mdi-camera",
                                    "show-size": true,
                                    rules: [v => !editingItem.value || v || imagePreview.value ? true : 'Image is required'],
                                    onChange: handleImageChange,
                                    placeholder: "Select an image for the reward"
                                  }, {
                                    selection: _withCtx(({ fileNames }) => [
                                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fileNames, (fileName) => {
                                        return (_openBlock(), _createBlock(_component_v_chip, {
                                          key: fileName,
                                          size: "small",
                                          label: "",
                                          color: "primary",
                                          class: "me-2"
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(fileName), 1)
                                          ]),
                                          _: 2
                                        }, 1024))
                                      }), 128))
                                    ]),
                                    _: 1
                                  }, 8, ["modelValue", "rules"]),
                                  (imagePreview.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                        _createElementVNode("div", _hoisted_13, [
                                          _createVNode(_component_v_icon, {
                                            icon: "mdi-eye",
                                            class: "mr-2",
                                            color: "primary"
                                          }),
                                          _cache[16] || (_cache[16] = _createElementVNode("span", { class: "text-body-2 font-weight-medium" }, "Image Preview", -1))
                                        ]),
                                        _createElementVNode("div", _hoisted_14, [
                                          _createElementVNode("img", {
                                            src: imagePreview.value,
                                            alt: "Preview",
                                            class: "preview-image"
                                          }, null, 8, _hoisted_15),
                                          _createVNode(_component_v_btn, {
                                            icon: "mdi-close",
                                            size: "small",
                                            color: "error",
                                            class: "position-absolute top-0 right-0 ma-1",
                                            onClick: clearImage
                                          })
                                        ])
                                      ]))
                                    : _createCommentVNode("", true)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_divider),
              _createVNode(_component_v_card_actions, { class: "pa-6" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    variant: "text",
                    size: "large",
                    onClick: closeStoreItemModal,
                    class: "px-6"
                  }, {
                    default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                      _createTextVNode(" Cancel ", -1)
                    ]))]),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    variant: "elevated",
                    size: "large",
                    onClick: saveStoreItem,
                    loading: saving.value,
                    class: "px-6",
                    "prepend-icon": "mdi-content-save"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(editingItem.value ? 'Save Changes' : 'Create Reward'), 1)
                    ]),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})